import apiClient, { api } from './api'
import { Medication, Prescription, PrescriptionCreate, LabResult, PaginatedResponse } from '@/types'
import type { LabFile, LabFileListResponse } from '@/types/file'

export interface PrescriptionSearchParams {
  patient_id?: string
  appointment_id?: string
  consultation_id?: string
  prescription_set_id?: string
  status?: string
  skip?: number
  limit?: number
}

export interface LabResultSearchParams {
  patient_id?: string
  appointment_id?: string
  consultation_id?: string
  status?: string
  skip?: number
  limit?: number
}

export interface LabOrderCreateRequest {
  patient_id: string
  appointment_id?: string | null
  consultation_id?: string | null
  lab_test_id: string
  priority?: 'routine' | 'urgent' | 'stat'
  clinical_indication?: string
}

export const pharmacyApi = {
  // Medications
  getMedications: async (search?: string, skip = 0, limit = 100): Promise<PaginatedResponse<Medication>> => {
    const searchParams = new URLSearchParams()
    if (search) searchParams.append('search', search)
    searchParams.append('skip', skip.toString())
    searchParams.append('limit', limit.toString())

    return api.get(`/pharmacy/medications?${searchParams.toString()}`)
  },

  getMedication: async (id: string): Promise<Medication> => {
    return api.get(`/pharmacy/medications/${id}`)
  },

  createMedication: async (data: any): Promise<Medication> => {
    return api.post('/pharmacy/medications', data)
  },

  updateMedication: async (id: string, data: any): Promise<Medication> => {
    return api.patch(`/pharmacy/medications/${id}`, data)
  },

  // Prescriptions
  getPrescriptions: async (params?: PrescriptionSearchParams): Promise<PaginatedResponse<Prescription>> => {
    const searchParams = new URLSearchParams()

    if (params?.patient_id) searchParams.append('patient_id', params.patient_id)
    if (params?.appointment_id) searchParams.append('appointment_id', params.appointment_id)
    if (params?.consultation_id) searchParams.append('consultation_id', params.consultation_id)
    if (params?.prescription_set_id) searchParams.append('prescription_set_id', params.prescription_set_id)
    if (params?.status) searchParams.append('status', params.status)
    if (params?.skip !== undefined) searchParams.append('skip', params.skip.toString())
    if (params?.limit !== undefined) searchParams.append('limit', params.limit.toString())

    return api.get(`/pharmacy/prescriptions?${searchParams.toString()}`)
  },

  getPrescription: async (id: string): Promise<Prescription> => {
    return api.get(`/pharmacy/prescriptions/${id}`)
  },

  createPrescription: async (data: PrescriptionCreate): Promise<Prescription> => {
    return api.post('/pharmacy/prescriptions', data)
  },

  updatePrescription: async (id: string, data: Partial<PrescriptionCreate>): Promise<Prescription> => {
    return api.patch(`/pharmacy/prescriptions/${id}`, data)
  },

  deletePrescription: async (id: string): Promise<void> => {
    return api.delete(`/pharmacy/prescriptions/${id}`)
  },

  // Lab Tests & Results
  getLabTests: async (): Promise<{ lab_tests: any[]; total: number }> => {
    return api.get('/pharmacy/lab-tests')
  },

  createLabTest: async (data: { test_name: string; test_category: string; specimen_type: string; cost: number }): Promise<any> => {
    return api.post('/pharmacy/lab-tests', data)
  },

  createLabOrder: async (data: LabOrderCreateRequest): Promise<LabResult> => {
    return api.post('/pharmacy/lab-orders', data)
  },

  getLabResults: async (params?: LabResultSearchParams): Promise<PaginatedResponse<LabResult>> => {
    const searchParams = new URLSearchParams()

    if (params?.patient_id) searchParams.append('patient_id', params.patient_id)
    if (params?.appointment_id) searchParams.append('appointment_id', params.appointment_id)
    if (params?.consultation_id) searchParams.append('consultation_id', params.consultation_id)
    if (params?.status) searchParams.append('status', params.status)
    if (params?.skip !== undefined) searchParams.append('skip', params.skip.toString())
    if (params?.limit !== undefined) searchParams.append('limit', params.limit.toString())

    return api.get(`/pharmacy/lab-results?${searchParams.toString()}`)
  },

  getLabResult: async (id: string): Promise<LabResult> => {
    return api.get(`/pharmacy/lab-results/${id}`)
  },

  updateLabResult: async (id: string, data: { result_value?: string; result_unit?: string; reference_range?: string; interpretation?: string; priority?: string; clinical_indication?: string }): Promise<LabResult> => {
    return api.patch(`/pharmacy/lab-results/${id}`, data)
  },

  // Prescription Metrics
  getPatientPrescriptionMetrics: async (patientId: string): Promise<any> => {
    return api.get(`/pharmacy/prescriptions/metrics/patient/${patientId}`)
  },

  getGlobalPrescriptionMetrics: async (period: string = '30d'): Promise<any> => {
    return api.get(`/pharmacy/prescriptions/metrics/global?period=${period}`)
  },

  downloadLabOrderPdf: async (resultId: string): Promise<Blob> => {
    const response = await apiClient.get(`/pharmacy/lab-results/${resultId}/order-pdf`, {
      responseType: 'blob',
    })
    return response.data
  },

  downloadConsultationPrescriptionPdf: async (consultationId: string): Promise<Blob> => {
    const response = await apiClient.get(
      `/pharmacy/prescriptions/by-consultation/${consultationId}/pdf`,
      {
        responseType: 'blob',
      }
    )
    return response.data
  },

  downloadPrescriptionSetPdf: async (prescriptionSetId: string): Promise<Blob> => {
    const response = await apiClient.get(`/pharmacy/prescriptions/by-set/${prescriptionSetId}/pdf`, {
      responseType: 'blob',
    })
    return response.data
  },

  downloadPrescriptionPdf: async (prescriptionId: string): Promise<Blob> => {
    const response = await apiClient.get(`/pharmacy/prescriptions/${prescriptionId}/pdf`, {
      responseType: 'blob',
    })
    return response.data
  },

  /** Get all lab files for a patient and filter for images. */
  getPatientLabFiles: async (patientId: string, limit = 20): Promise<LabFile[]> => {
    const params = new URLSearchParams({ patient_id: patientId, limit: limit.toString() })
    const result = await api.get<LabFileListResponse>(`/files/patient/${patientId}?${params}`)
    return result.files
  },
}
